import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Option, Select, Textarea } from '@material-tailwind/react';
import { PaperclipIcon, XIcon } from 'lucide-react';
import MaintenanceModal from './MaintenanceModal';
import MultiLocationPicker from './MultiLocationPicker';
import { useGetUsers } from '../../../custom-hooks/use-users';
import { useAddWorkOrder, useUpdateWorkOrder } from '../../../custom-hooks/use-maintenance';
import { MAINTENANCE_CATEGORIES_ENUM, MAINTENANCE_PRIORITIES_ENUM, STATIC_USER_ROLES } from '../../../utils/enums';
import { Notify, NotifyError } from '../../../utils/functions';
import { CATEGORY_META, PRIORITY_META, getLocations } from '../utils';

const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024; // 5MB — same cap as the Leads module
const ALLOWED_ATTACHMENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'text/csv',
];

// Matches the Leads module's attachment pattern (leadFormModal/basicInfoTab.js) —
// files are read as base64 data URIs and stored directly on the document, rather
// than uploaded to Firebase Storage, which nothing else in this app's forms uses.
const readFileAsAttachment = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, url: reader.result, mimetype: file.type, size: file.size, uploadedAt: new Date().toISOString() });
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const EMPTY_FORM = {
    title: '', locations: [],
    category: MAINTENANCE_CATEGORIES_ENUM[0], description: '',
    estimate: '', priority: 'medium', dueDate: '', assignee: '', attachments: [],
};

const WorkOrderFormModal = ({ isOpen, onClose, workOrder }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [uploading, setUploading] = useState(false);
    const isUpdating = !!workOrder;

    const addMutation = useAddWorkOrder();
    const updateMutation = useUpdateWorkOrder();

    const { data: staffData } = useGetUsers({
        role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE, STATIC_USER_ROLES.STAFF],
        _page: 1,
        _limit: 1000,
    });
    const staff = staffData?.users || [];

    useEffect(() => {
        if (workOrder) {
            setForm({
                title: workOrder.title || '',
                // Flattened back to plain ids, plus the floor number the picker shows in
                // its list — the saved document only stores the three refs.
                locations: getLocations(workOrder).map((loc) => ({
                    branch: loc.branch?._id || loc.branch || '',
                    building: loc.building?._id || loc.building || '',
                    floor: loc.floor?._id || loc.floor || '',
                    space: loc.space || '',
                    floorNumber: loc.floor?.number ?? null,
                })),
                category: workOrder.category,
                description: workOrder.description || '',
                estimate: workOrder.estimate ?? '',
                priority: workOrder.priority,
                dueDate: workOrder.dueDate ? workOrder.dueDate.slice(0, 10) : '',
                assignee: workOrder.assignee?._id || '',
                attachments: workOrder.attachments || [],
            });
        } else if (isOpen) {
            setForm(EMPTY_FORM);
        }
    }, [workOrder, isOpen]);

    const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const valid = files.filter((file) => {
            if (file.size > MAX_ATTACHMENT_SIZE) {
                NotifyError(`${file.name} is too large. Maximum size allowed is 5MB.`);
                return false;
            }
            if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
                NotifyError(`${file.name}: invalid file type. Only PDF, Word, JPEG, PNG, and CSV are allowed.`);
                return false;
            }
            return true;
        });
        if (!valid.length) { e.target.value = ''; return; }

        setUploading(true);
        try {
            const uploaded = await Promise.all(valid.map(readFileAsAttachment));
            setForm((prev) => ({ ...prev, attachments: [...prev.attachments, ...uploaded] }));
            Notify(`${uploaded.length} file(s) attached`, 'success');
        } catch (error) {
            NotifyError('Failed to read file', error);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const removeAttachment = (index) => setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));

    // Two guards, because they catch different things. `isSaving` disables the button
    // once React re-renders; `submittingRef` catches clicks (or repeated Enter presses)
    // that land in the same tick, before that re-render commits — which is how the same
    // work order was getting created several times over.
    const isSaving = addMutation.isLoading || updateMutation.isLoading;
    const submittingRef = useRef(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        submittingRef.current = true;

        const payload = {
            title: form.title,
            // floorNumber is a display-only field the picker carries; the server ignores
            // it, but there's no reason to put it on the wire.
            locations: form.locations.map(({ branch, building, floor, space }) => ({ branch, building, floor, space })),
            category: form.category,
            description: form.description,
            estimate: form.estimate === '' ? null : Number(form.estimate),
            priority: form.priority,
            dueDate: form.dueDate,
            assignee: form.assignee || null,
            attachments: form.attachments,
        };

        try {
            if (isUpdating) {
                await updateMutation.mutateAsync({ workOrderId: workOrder._id, data: payload });
            } else {
                await addMutation.mutateAsync(payload);
            }
            onClose();
        } finally {
            // Released in `finally` so a failed save re-enables the form instead of
            // leaving it permanently stuck. The modal stays open on failure.
            submittingRef.current = false;
        }
    };

    // `estimate` deliberately absent — a request can be logged before it's costed. The
    // status gate asks for it when the work order leaves To Do.
    const isValid = form.title && form.locations.length > 0 && form.category && form.priority && form.dueDate;

    return (
        <MaintenanceModal isOpen={isOpen} onClose={onClose} title={isUpdating ? 'Edit Work Order' : 'Create Work Order'} maxWidth="xl">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Input label="Title *" value={form.title} onChange={(e) => handleChange('title', e.target.value)} autoFocus />

                <div>
                    <p className="mb-1.5 text-xs font-semibold text-inv-ink">Location{form.locations.length > 1 ? 's' : ''} *</p>
                    <MultiLocationPicker
                        locations={form.locations}
                        onChange={(locations) => handleChange('locations', locations)}
                    />
                </div>

                <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="flex-1">
                        <Select
                            label="Category *"
                            value={form.category}
                            onChange={(v) => handleChange('category', v)}
                            selected={() => CATEGORY_META[form.category]?.label || form.category}
                        >
                            {MAINTENANCE_CATEGORIES_ENUM.map((cat) => (
                                <Option key={cat} value={cat}>{CATEGORY_META[cat]?.label || cat}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className="flex-1">
                        <Select
                            label="Priority *"
                            value={form.priority}
                            onChange={(v) => handleChange('priority', v)}
                            selected={() => PRIORITY_META[form.priority]?.label || form.priority}
                        >
                            {MAINTENANCE_PRIORITIES_ENUM.map((p) => (
                                <Option key={p} value={p}>{PRIORITY_META[p]?.label || p}</Option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="flex-1">
                        <Input type="number" label="Estimate (PKR)" value={form.estimate} onChange={(e) => handleChange('estimate', e.target.value)} min="0" />
                    </div>
                    <div className="flex-1">
                        <Input type="date" label="Due Date *" value={form.dueDate} onChange={(e) => handleChange('dueDate', e.target.value)} />
                    </div>
                </div>

                <Select
                    label="Assignee (optional)"
                    value={form.assignee}
                    onChange={(v) => handleChange('assignee', v)}
                    menuProps={{ className: 'max-h-64 overflow-y-auto' }}
                    selected={() => staff.find((u) => u._id === form.assignee)?.name || ''}
                >
                    {staff.map((u) => <Option key={u._id} value={u._id}>{u.name || u.email}</Option>)}
                </Select>

                <Textarea label="Description (optional)" rows={3} value={form.description} onChange={(e) => handleChange('description', e.target.value)} />

                <div>
                    <label className="flex w-fit cursor-pointer items-center gap-2 rounded-full border border-inv-cream-200 px-3 py-1.5 text-xs font-semibold text-inv-ink transition-colors hover:bg-inv-cream-100">
                        <PaperclipIcon size={14} />
                        {uploading ? 'Uploading…' : 'Attach photos / files'}
                        <input
                            type="file"
                            multiple
                            hidden
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.csv"
                            onChange={handleFilesSelected}
                            disabled={uploading}
                        />
                    </label>
                    {form.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {form.attachments.map((att, index) => (
                                <span key={`${att.name}-${index}`} className="flex items-center gap-1.5 rounded-full bg-inv-cream-100 px-3 py-1 text-xs text-inv-ink">
                                    {att.name}
                                    <XIcon size={12} className="cursor-pointer" onClick={() => removeAttachment(index)} />
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="text" className="rounded-full" onClick={onClose} type="button" disabled={isSaving}>Cancel</Button>
                    <Button
                        type="submit"
                        className="rounded-full bg-inv-brand shadow-none hover:bg-inv-brand-dark"
                        disabled={!isValid || uploading || isSaving}
                        loading={isSaving}
                    >
                        {isUpdating ? 'Save Changes' : 'Create Work Order'}
                    </Button>
                </div>
            </form>
        </MaintenanceModal>
    );
};

export default WorkOrderFormModal;
